import { userMessage } from '@rr-crm/shared/api-errors';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { RrButton, RrModal } from '@rr-crm/shared/components';

import {
  useAdvanceOnboardingStep,
  useUserProfile,
  type OnboardingStep,
  type UserProfile,
} from '../api/useUserProfile';

/**
 * Phase 10C — first-use onboarding flow.
 *
 * Shown once, for users whose `hasCompletedOnboarding` is false. Walks the rep
 * through: welcome → signature (links to profile settings) → tour →
 * first-activity → done. Advancing each step PATCHes
 * /me/profile/onboarding-state; the final "done" advance flips
 * hasCompletedOnboarding server-side, which removes the flow on next load.
 *
 * A "Skip for now" affordance jumps straight to `done` so a rep who doesn't
 * want the walkthrough isn't trapped. Re-showing the flow is a superAdmin
 * action (POST /admin/users/:userId/onboarding-reset).
 *
 * PLACEHOLDER COPY — Kenny to refine. All user-visible strings below are
 * first-draft scaffolding; the flow shape + wiring is the deliverable.
 */

// The ordered step sequence the modal walks. `done` is terminal (it flips
// hasCompletedOnboarding) and is never a "current" panel.
const FLOW_STEPS: ReadonlyArray<Exclude<OnboardingStep, 'done'>> = [
  'welcome',
  'signature',
  'tour',
  'firstActivity',
];

interface StepCopy {
  title: string;
  body: JSX.Element;
}

// PLACEHOLDER COPY — Kenny to refine.
const STEP_COPY: Record<Exclude<OnboardingStep, 'done'>, StepCopy> = {
  welcome: {
    title: 'Welcome to The Right Role™ CRM',
    body: (
      <p className="text-sm text-rr-fg-secondary">
        {/* PLACEHOLDER COPY — Kenny to refine */}
        Let&apos;s get you set up. This quick tour covers your email signature, where to find things,
        and how to log your first activity. It takes about a minute.
      </p>
    ),
  },
  signature: {
    title: 'Set up your email signature', // PLACEHOLDER COPY — Kenny to refine
    body: (
      <p className="text-sm text-rr-fg-secondary">
        {/* PLACEHOLDER COPY — Kenny to refine */}
        Your signature is appended to customer emails. You can set it now from{' '}
        <Link to="/settings/profile" className="text-rr-accent-primary underline">
          My profile
        </Link>
        , or skip and do it later.
      </p>
    ),
  },
  tour: {
    title: 'Find your way around', // PLACEHOLDER COPY — Kenny to refine
    body: (
      <p className="text-sm text-rr-fg-secondary">
        {/* PLACEHOLDER COPY — Kenny to refine */}
        Use the left nav: <strong>Accounts</strong> for your book of business,{' '}
        <strong>Tasks</strong> for follow-ups, and <strong>Help</strong> any time you&apos;re stuck.
      </p>
    ),
  },
  firstActivity: {
    title: 'Log your first activity', // PLACEHOLDER COPY — Kenny to refine
    body: (
      <p className="text-sm text-rr-fg-secondary">
        {/* PLACEHOLDER COPY — Kenny to refine */}
        Open any account and use <strong>Quick Log</strong> to record a call, email, or note. That&apos;s
        how the pipeline stays current.
      </p>
    ),
  },
};

export interface OnboardingFlowProps {
  /** Called once the flow completes or is skipped (parent unmounts/hides it). */
  onComplete?: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps): JSX.Element | null {
  const profileQuery = useUserProfile();
  const advance = useAdvanceOnboardingStep();
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Session-only dismissal. Closing via the X / backdrop / "Skip for now" does
  // NOT persist completion — onboarding reappears on the next login until the
  // rep genuinely finishes the flow. (Previously these called recordStep('done'),
  // permanently completing onboarding from a stray backdrop click.)
  const [dismissed, setDismissed] = useState(false);

  // RIG-236 — the flow is a focused first-login overlay mounted once in the app
  // shell, so it persists across route changes. If the rep navigates away — e.g.
  // clicks the "My profile" link in the signature step, or uses the left nav —
  // close the overlay instead of leaving it stuck on top of (and visually
  // stacking over) the new page. Session-only dismiss: it reappears on the next
  // login until onboarding is genuinely completed.
  const location = useLocation();
  const mountPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== mountPathRef.current) setDismissed(true);
  }, [location.pathname]);

  const profile = profileQuery.data;

  // Gate: only show for a loaded profile that hasn't completed onboarding, and
  // not after a session dismissal.
  if (!profile || profile.hasCompletedOnboarding || dismissed) return null;

  const currentStep = FLOW_STEPS[stepIndex];
  const isLastStep = stepIndex === FLOW_STEPS.length - 1;

  // Advance the named step server-side at the given version. Returns the updated
  // profile (whose `version` has incremented) on success, or null on failure.
  // The caller MUST thread the returned version into any follow-up PATCH in the
  // same handler — otherwise the second write reuses the stale render-time
  // version and the server rejects it with 412 (E2E-R2 #1: the final step fired
  // recordStep(currentStep) then recordStep('done') both at profile.version).
  const recordStep = async (
    step: OnboardingStep,
    expectedVersion: number,
  ): Promise<UserProfile | null> => {
    setError(null);
    try {
      return await advance.mutateAsync({ expectedVersion, step });
    } catch (err) {
      // QA 2026-10-01 F-28: plain sentence, never the raw error text.
      setError(userMessage(err));
      return null;
    }
  };

  const handleNext = async (): Promise<void> => {
    if (!currentStep) return;
    const afterStep = await recordStep(currentStep, profile.version);
    if (!afterStep) return;
    if (isLastStep) {
      // Flip the terminal flag using the FRESH version returned by the step we
      // just recorded (not the stale render-time profile.version).
      const afterDone = await recordStep('done', afterStep.version);
      if (afterDone) onComplete?.();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const handleDismiss = (): void => {
    // Session-only: hide for now, no server write, no confirmation. Reappears
    // next login until onboarding is genuinely completed via the flow.
    setDismissed(true);
    onComplete?.();
  };

  const copy = currentStep ? STEP_COPY[currentStep] : undefined;

  return (
    <RrModal
      open
      onClose={handleDismiss}
      title={copy?.title ?? 'Getting started'}
      footer={
        <>
          <RrButton variant="tertiary" onClick={handleDismiss} disabled={advance.isPending}>
            {/* Session-only dismiss — reappears next login until completed. */}
            Skip for now
          </RrButton>
          <RrButton variant="primary" onClick={() => void handleNext()} disabled={advance.isPending}>
            {/* PLACEHOLDER COPY — Kenny to refine */}
            {advance.isPending ? 'Saving…' : isLastStep ? 'Finish' : 'Next'}
          </RrButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Step progress dots */}
        <div className="flex gap-1.5" aria-hidden>
          {FLOW_STEPS.map((s, i) => (
            <span
              key={s}
              className={`h-1.5 w-6 rounded-full ${
                i <= stepIndex ? 'bg-rr-accent-primary' : 'bg-rr-border-subtle'
              }`}
            />
          ))}
        </div>
        {copy?.body}
        {error !== null && (
          <div className="rounded-md border border-rr-border-default bg-rr-surface-raised p-2 text-xs text-rr-error">
            {/* PLACEHOLDER COPY — Kenny to refine */}
            Couldn&apos;t save that step ({error}). You can try again or skip.
          </div>
        )}
      </div>
    </RrModal>
  );
}
